import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useTPRExceptionsActions } from "tap/store/tprExceptions/useTPRExceptions.js";
import { tapApi } from "tap/api.js";
import { filterCriteria } from "tap/utils/filterCriteria.js";
import {
  buildDismissByRowFromSession,
  mergeDismissByUserOptions,
} from "../utils/userOptionsUtils.js";

// Minimum time to show the loading spinner (in ms)
const MIN_LOADING_DURATION = 400;

let userOptionsRequestId = 0;
let tprExceptionsFetchId = 0;

export function beginTprExceptionsFetch() {
  return ++tprExceptionsFetchId;
}

export function isLatestTprExceptionsFetch(id) {
  return id === tprExceptionsFetchId;
}

/**
 * Custom hook that consolidates TPR Exceptions data fetching logic.
 * Handles loading states, error toasts, and Redux updates.
 *
 * @returns {Object} Object containing fetchTPRExceptions function and state values
 */
export function useTPRExceptionsData() {
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();
  const { updateTPRExceptions, clearTPRExceptions } = useTPRExceptionsActions();

  const {
    filterForm,
    tprExceptionsRange,
    tprExceptionsSort,
    itemsPerPage,
    defaultFilterRange,
    userOptions,
  } = useSelector((s) => s.tprExceptions);

  /**
   * Fetches TPR Exceptions from the API with the given range.
   * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
   * @param {string} range - The pagination range (e.g., "0,25")
   * @param {Object|null} customFilterData - Optional filter data to use instead of Redux filterForm
   * @param {Array|null} customSort - Optional sort array to use instead of Redux tprExceptionsSort
   * @returns {Promise<Object|null>} The API response data or null on error
   */
  const fetchTPRExceptions = useCallback(
    async (range, customFilterData = null, customSort = null) => {
      const startTime = Date.now();
      setLoadingTrue();

      // Merge URL / partial filter with Redux so defaults (e.g. mineOnly: true) apply
      // when the caller passes {} from an empty search string. Plain ?? would treat {} as set.
      const filterData =
        customFilterData == null
          ? filterForm
          : { ...filterForm, ...customFilterData };
      const sortData = customSort ?? tprExceptionsSort;

      try {
        const data = await tapApi.tprExceptions(
          filterCriteria(filterData, "", "tprExceptions"),
          range,
          sortData,
        );

        if (data?.code || data?.httpCode || data?.error) {
          showErrorToast(
            data?.message || "Failed to fetch TPR Exceptions data.",
          );
          return null;
        }

        return data;
      } catch (error) {
        showErrorToast(
          error?.message || "Failed to fetch TPR Exceptions data.",
        );
        console.error("Error fetching TPR Exceptions:", error);
        return null;
      } finally {
        // Ensure minimum loading duration for consistent UX
        const elapsed = Date.now() - startTime;
        const remainingTime = MIN_LOADING_DURATION - elapsed;

        if (remainingTime > 0) {
          setTimeout(() => setLoadingFalse(), remainingTime);
        } else {
          setLoadingFalse();
        }
      }
    },
    [
      filterForm,
      tprExceptionsSort,
      setLoadingTrue,
      setLoadingFalse,
      showErrorToast,
    ],
  );

  /**
   * Fetches users who have dismissed TPR exceptions for the "Dismissed By" filter.
   * @param {Object} [options]
   * @param {boolean} [options.cacheBust=false] - Bypass cached /users responses
   * @returns {Promise<Array|null>} User options array, or null if superseded
   */
  const loadUserOptions = useCallback(async ({ cacheBust = false } = {}) => {
    const requestId = ++userOptionsRequestId;

    try {
      const userRes = await tapApi.users(
        { dataType: "tprExceptions" },
        [0, 199],
        ["fullName", "ASC"],
        { cacheBust },
      );

      if (requestId !== userOptionsRequestId) {
        return null;
      }

      return (
        userRes?.content?.map(({ fullName, id }) => ({
          label: fullName,
          value: id,
        })) ?? []
      );
    } catch (error) {
      console.error("Failed to load user options:", error);
      if (requestId === userOptionsRequestId) {
        return [];
      }
      return null;
    }
  }, []);

  /**
   * Refreshes Dismissed By options from /users and visible grid rows.
   * Grid rows supply dismissBy immediately after dismiss, even when /users is cached.
   * @param {Array<Object>} exceptions - Current exception rows from the grid fetch
   * @returns {Promise<Array>} Merged user options written to Redux
   */
  const syncUserOptions = useCallback(
    async (exceptions = []) => {
      const apiUsers = (await loadUserOptions({ cacheBust: true })) ?? [];
      const merged = mergeDismissByUserOptions(
        userOptions,
        apiUsers,
        exceptions,
      );

      updateTPRExceptions({
        userOptions: merged,
        userOptionsRevision: Date.now(),
      });

      return merged;
    },
    [loadUserOptions, updateTPRExceptions, userOptions],
  );

  /**
   * Immediately adds the logged-in user to Dismissed By options in Redux.
   * Synchronous so the dropdown updates before any network refresh.
   * @returns {boolean} Whether the session user was appended
   */
  const appendSessionUserToDismissedByFilter = useCallback(() => {
    const dismissByRow = buildDismissByRowFromSession();
    if (!dismissByRow) return false;

    const merged = mergeDismissByUserOptions(userOptions, [], [dismissByRow]);
    updateTPRExceptions({
      userOptions: merged,
      userOptionsRevision: Date.now(),
    });
    return true;
  }, [updateTPRExceptions, userOptions]);

  return {
    fetchTPRExceptions,
    loadUserOptions,
    syncUserOptions,
    appendSessionUserToDismissedByFilter,
    updateTPRExceptions,
    clearTPRExceptions,
    itemsPerPage,
    tprExceptionsRange,
    tprExceptionsSort,
    filterForm,
    defaultFilterRange,
  };
}
